<template>
  <Teleport to="#modalTarget">
    <div
      class="modal"
      v-if="showModal"
      tabindex="-1"
      aria-labelledby="createEditDraftModalLabel"
      aria-hidden="true"
      style="z-index: 1060"
    >
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="createEditDraftModalLabel">
            <font-awesome-icon icon="edit" class="me-2" />
            {{ isEditing ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen' }} ({{
              teamTemplate?.customName || teamTemplate?.template?.name
            }})
          </h5>
          <div class="ms-auto d-flex align-items-center gap-5">
            <select id="draftType" v-model="form.type" class="form-select" @change="onTypeChange">
              <option value="">Typ auswählen...</option>
              <option value="manual">Manuell</option>
              <option value="excel">Excel</option>
            </select>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveDraft">
            <!-- Basic Information -->

            <!-- No Type Selected Info -->
            <div v-if="!form.type" class="text-center py-5">
              <font-awesome-icon icon="info-circle" size="3x" class="text-muted mb-3" />
              <h5 class="text-muted mb-3">Bitte wähle einen Typ aus</h5>
              <p class="text-muted">
                Wähle oben rechts einen Typ aus, um mit der Erstellung der Vorlage zu beginnen.
              </p>
              <div class="row justify-content-center mt-4">
                <div class="col-md-10">
                  <div class="row g-3 justify-content-center">
                    <div class="col-md-4 col-lg-3">
                      <div
                        class="card border-0 bg-light type-card"
                        @click="selectType('manual')"
                        role="button"
                        tabindex="0"
                        @keydown.enter="selectType('manual')"
                        @keydown.space="selectType('manual')"
                      >
                        <div
                          class="card-body text-center d-flex flex-column justify-content-center"
                        >
                          <font-awesome-icon icon="edit" size="2x" class="text-primary mb-2" />
                          <h6>Manuell</h6>
                          <small class="text-muted">Fülle die Felder direkt aus</small>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4 col-lg-3">
                      <div
                        class="card border-0 bg-light type-card"
                        @click="selectType('excel')"
                        role="button"
                        tabindex="0"
                        @keydown.enter="selectType('excel')"
                        @keydown.space="selectType('excel')"
                      >
                        <div
                          class="card-body text-center d-flex flex-column justify-content-center"
                        >
                          <font-awesome-icon
                            icon="file-excel"
                            size="2x"
                            class="text-success mb-2"
                          />
                          <h6>Excel</h6>
                          <small class="text-muted">Download Template und Upload</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Manual Configuration -->
            <div v-if="form.type === 'manual'" class="card mb-3">
              <div class="card-header">
                <h6 class="mb-0">
                  <font-awesome-icon icon="edit" class="me-2" />
                  Manuelle Dateneingabe
                </h6>
              </div>
              <div class="card-body">
                <div class="alert alert-info">
                  <font-awesome-icon icon="info-circle" class="me-2" />
                  <strong>Manuelle Eingabe:</strong> Fülle die Template-Felder manuell aus und
                  speichere die Vorlage. Es können Felder leer gelassen werden. Sollte ein
                  Datumsfeld befüllt sein, wird die Vorlage nach Ablauf dieses Datums gelöscht.
                </div>

                <!-- Template Fields by Category -->
                <div
                  v-if="
                    templateFieldsByCategory.personal.length > 0 ||
                    templateFieldsByCategory.receipt.length > 0 ||
                    templateFieldsByCategory.financial.length > 0
                  "
                >
                  <!-- Personal Fields -->
                  <div v-if="templateFieldsByCategory.personal.length > 0" class="mb-4">
                    <h6 class="mb-3">
                      <font-awesome-icon icon="user" class="me-2" />
                      Persönliche Informationen
                    </h6>
                    <div class="row">
                      <div
                        v-for="field in templateFieldsByCategory.personal"
                        :key="field.id"
                        :class="field.type === 'file' ? 'col-12' : 'col-md-6'"
                      >
                        <div class="mb-3">
                          <label :for="`field-${field.id}`" class="form-label">
                            {{ field.displayName }}
                            <span v-if="field.required" class="text-danger">*</span>
                          </label>

                          <!-- Text Input -->
                          <input
                            v-if="field.type === 'text'"
                            v-model="manualData[field.name]"
                            type="text"
                            class="form-control"
                            :id="`field-${field.id}`"
                            :placeholder="field.placeholder || field.displayName"
                          />

                          <!-- Number Input -->
                          <input
                            v-else-if="field.type === 'number'"
                            v-model.number="manualData[field.name]"
                            type="number"
                            class="form-control"
                            :id="`field-${field.id}`"
                            :placeholder="field.placeholder || field.displayName"
                            step="0.01"
                          />

                          <!-- Date Input -->
                          <input
                            v-else-if="field.type === 'date'"
                            v-model="manualData[field.name]"
                            type="date"
                            class="form-control"
                            :id="`field-${field.id}`"
                          />

                          <!-- Select Input -->
                          <select
                            v-else-if="field.type === 'select'"
                            v-model="manualData[field.name]"
                            class="form-select"
                            :id="`field-${field.id}`"
                          >
                            <option value="">-- Bitte wählen --</option>
                            <option
                              v-for="option in field.options?.options ||
                              field.options?.choices ||
                              []"
                              :key="option.value || option"
                              :value="option.value || option"
                            >
                              {{ option.label || option }}
                            </option>
                          </select>

                          <!-- Checkbox Input -->
                          <div v-else-if="field.type === 'checkbox'" class="form-check">
                            <input
                              v-model="manualData[field.name]"
                              type="checkbox"
                              class="form-check-input"
                              :id="`field-${field.id}`"
                            />
                            <label :for="`field-${field.id}`" class="form-check-label">
                              {{ field.displayName }}
                            </label>
                          </div>

                          <!-- Textarea Input -->
                          <textarea
                            v-else-if="field.type === 'textarea'"
                            v-model="manualData[field.name]"
                            class="form-control"
                            :id="`field-${field.id}`"
                            :placeholder="field.placeholder || field.displayName"
                            rows="3"
                          ></textarea>

                          <!-- Default Text Input -->
                          <input
                            v-else
                            v-model="manualData[field.name]"
                            type="text"
                            class="form-control"
                            :id="`field-${field.id}`"
                            :placeholder="field.placeholder || field.displayName"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Receipt Fields -->
                  <div v-if="templateFieldsByCategory.receipt.length > 0" class="mb-4">
                    <h6 class="mb-3">
                      <font-awesome-icon icon="receipt" class="me-2" />
                      Quittungsinformationen
                    </h6>
                    <div class="row">
                      <div
                        v-for="field in templateFieldsByCategory.receipt"
                        :key="field.id"
                        :class="field.type === 'file' ? 'col-12' : 'col-md-6'"
                      >
                        <div class="mb-3">
                          <label :for="`field-${field.id}`" class="form-label">
                            {{ field.displayName }}
                            <span v-if="field.required" class="text-danger">*</span>
                          </label>

                          <!-- Text Input -->
                          <input
                            v-if="field.type === 'text'"
                            v-model="manualData[field.name]"
                            type="text"
                            class="form-control"
                            :id="`field-${field.id}`"
                            :placeholder="field.placeholder || field.displayName"
                          />

                          <!-- Number Input -->
                          <input
                            v-else-if="field.type === 'number'"
                            v-model.number="manualData[field.name]"
                            type="number"
                            class="form-control"
                            :id="`field-${field.id}`"
                            :placeholder="field.placeholder || field.displayName"
                            step="0.01"
                          />

                          <!-- Date Input -->
                          <input
                            v-else-if="field.type === 'date'"
                            v-model="manualData[field.name]"
                            type="date"
                            class="form-control"
                            :id="`field-${field.id}`"
                          />

                          <!-- Select Input -->
                          <select
                            v-else-if="field.type === 'select'"
                            v-model="manualData[field.name]"
                            class="form-select"
                            :id="`field-${field.id}`"
                          >
                            <option value="">-- Bitte wählen --</option>
                            <option
                              v-for="option in field.options?.options ||
                              field.options?.choices ||
                              []"
                              :key="option.value || option"
                              :value="option.value || option"
                            >
                              {{ option.label || option }}
                            </option>
                          </select>

                          <!-- Checkbox Input -->
                          <div v-else-if="field.type === 'checkbox'" class="form-check">
                            <input
                              v-model="manualData[field.name]"
                              type="checkbox"
                              class="form-check-input"
                              :id="`field-${field.id}`"
                            />
                            <label :for="`field-${field.id}`" class="form-check-label">
                              {{ field.displayName }}
                            </label>
                          </div>

                          <!-- Textarea Input -->
                          <textarea
                            v-else-if="field.type === 'textarea'"
                            v-model="manualData[field.name]"
                            class="form-control"
                            :id="`field-${field.id}`"
                            :placeholder="field.placeholder || field.displayName"
                            rows="3"
                          ></textarea>

                          <!-- Default Text Input -->
                          <input
                            v-else
                            v-model="manualData[field.name]"
                            type="text"
                            class="form-control"
                            :id="`field-${field.id}`"
                            :placeholder="field.placeholder || field.displayName"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Financial Fields -->
                  <div v-if="templateFieldsByCategory.financial.length > 0" class="mb-4">
                    <h6 class="mb-3">
                      <font-awesome-icon icon="euro-sign" class="me-2" />
                      Finanzielle Informationen
                    </h6>
                    <div class="row">
                      <div
                        v-for="field in templateFieldsByCategory.financial"
                        :key="field.id"
                        :class="field.type === 'file' ? 'col-12' : 'col-md-6'"
                      >
                        <div class="mb-3">
                          <label :for="`field-${field.id}`" class="form-label">
                            {{ field.displayName }}
                            <span v-if="field.required" class="text-danger">*</span>
                          </label>

                          <!-- Text Input -->
                          <input
                            v-if="field.type === 'text'"
                            v-model="manualData[field.name]"
                            type="text"
                            class="form-control"
                            :id="`field-${field.id}`"
                            :placeholder="field.placeholder || field.displayName"
                          />

                          <!-- Number Input -->
                          <input
                            v-else-if="field.type === 'number'"
                            v-model.number="manualData[field.name]"
                            type="number"
                            class="form-control"
                            :id="`field-${field.id}`"
                            :placeholder="field.placeholder || field.displayName"
                            step="0.01"
                          />

                          <!-- Date Input -->
                          <input
                            v-else-if="field.type === 'date'"
                            v-model="manualData[field.name]"
                            type="date"
                            class="form-control"
                            :id="`field-${field.id}`"
                          />

                          <!-- Select Input -->
                          <select
                            v-else-if="field.type === 'select'"
                            v-model="manualData[field.name]"
                            class="form-select"
                            :id="`field-${field.id}`"
                          >
                            <option value="">-- Bitte wählen --</option>
                            <option
                              v-for="option in field.options?.options ||
                              field.options?.choices ||
                              []"
                              :key="option.value || option"
                              :value="option.value || option"
                            >
                              {{ option.label || option }}
                            </option>
                          </select>

                          <!-- Checkbox Input -->
                          <div v-else-if="field.type === 'checkbox'" class="form-check">
                            <input
                              v-model="manualData[field.name]"
                              type="checkbox"
                              class="form-check-input"
                              :id="`field-${field.id}`"
                            />
                            <label :for="`field-${field.id}`" class="form-check-label">
                              {{ field.displayName }}
                            </label>
                          </div>

                          <!-- Textarea Input -->
                          <textarea
                            v-else-if="field.type === 'textarea'"
                            v-model="manualData[field.name]"
                            class="form-control"
                            :id="`field-${field.id}`"
                            :placeholder="field.placeholder || field.displayName"
                            rows="3"
                          ></textarea>

                          <!-- Default Text Input -->
                          <input
                            v-else
                            v-model="manualData[field.name]"
                            type="text"
                            class="form-control"
                            :id="`field-${field.id}`"
                            :placeholder="field.placeholder || field.displayName"
                          />

                          <!-- Tax Rate Selection (for fields with includeTaxRate) -->
                          <div v-if="field.options?.includeTaxRate" class="mt-2">
                            <label class="form-label small text-muted">Mehrwertsteuersatz</label>

                            <!-- User Choice: Dropdown selection -->
                            <select
                              v-if="field.options?.taxRateSelectionMode === 'user_choice'"
                              v-model="manualData[`${field.name}_taxRate`]"
                              class="form-select form-select-sm"
                              :id="`field-${field.id}-taxrate`"
                            >
                              <option value="">-- MwSt-Satz wählen --</option>
                              <option
                                v-for="taxRate in field.options?.resolvedTaxRates || []"
                                :key="taxRate.id"
                                :value="taxRate.id"
                              >
                                {{ taxRate.displayName }} ({{ taxRate.rate }}%)
                              </option>
                            </select>

                            <!-- Pre-selected: Display only -->
                            <div
                              v-else-if="field.options?.selectedTaxRate"
                              class="form-control form-control-sm bg-light"
                            >
                              <small class="text-muted">
                                <font-awesome-icon icon="info-circle" class="me-1" />
                                {{ field.options.selectedTaxRate.displayName }} ({{
                                  field.options.selectedTaxRate.rate
                                }}%)
                              </small>
                            </div>

                            <!-- Fallback: No tax rates available -->
                            <div v-else class="form-control form-control-sm bg-light">
                              <small class="text-muted">
                                <font-awesome-icon icon="exclamation-triangle" class="me-1" />
                                Keine Steuersätze verfügbar
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="text-center py-4 text-muted">
                  <font-awesome-icon icon="info-circle" size="2x" class="mb-3" />
                  <p>Keine Template-Felder verfügbar. Bitte wähle ein Template aus.</p>
                </div>
              </div>
            </div>


            <!-- Excel Configuration -->
            <div v-if="form.type === 'excel'" class="card mb-3">
              <div class="card-header">
                <h6 class="mb-0">
                  <font-awesome-icon icon="file-excel" class="me-2" />
                  Excel-Import
                </h6>
              </div>
              <div class="card-body">
                <div class="alert alert-info">
                  <font-awesome-icon icon="info-circle" class="me-2" />
                  <strong>Excel-Vorlage:</strong> Lade eine Excel-Vorlage herunter, die alle
                  verfügbaren Felder des Templates enthält.
                </div>

                <!-- Download Excel Template Button -->
                <div class="mb-3">
                  <button
                    type="button"
                    class="btn btn-outline-success"
                    @click="downloadExcelTemplate"
                    :disabled="!templateFields.length"
                  >
                    <font-awesome-icon icon="download" class="me-2" />
                    Excel-Vorlage herunterladen
                  </button>
                  <div class="form-text">
                    Die Excel-Datei enthält eine Spalte mit allen Feldnamen des Templates.
                  </div>
                </div>

                <!-- Upload Excel File -->
                <div class="mb-3">
                  <label for="excelFileInput" class="form-label">Excel-Datei hochladen</label>
                  <input
                    id="excelFileInput"
                    type="file"
                    class="form-control"
                    accept=".xlsx,.xls"
                    @change="onExcelFileSelected"
                  />
                  <div class="form-text">
                    Lade die ausgefüllte Excel-Datei hoch, um Vorlagen zu erstellen.
                  </div>

                  <!-- Upload Button - wird nur angezeigt wenn eine Datei ausgewählt wurde -->
                  <div v-if="selectedFile" class="mt-3">
                    <div class="d-flex align-items-center gap-3">
                      <div class="flex-grow-1">
                        <div class="d-flex align-items-center">
                          <font-awesome-icon icon="file-excel" class="text-success me-2" />
                          <span class="fw-medium">{{ selectedFile.name }}</span>
                          <small class="text-muted ms-2"
                            >({{ (selectedFile.size / 1024).toFixed(1) }} KB)</small
                          >
                        </div>
                      </div>
                      <button
                        type="button"
                        class="btn btn-success"
                        @click="uploadExcelFile"
                        :disabled="uploading"
                      >
                        <span v-if="uploading" class="spinner-border spinner-border-sm me-2"></span>
                        <font-awesome-icon v-else icon="upload" class="me-2" />
                        {{ uploading ? 'Wird hochgeladen...' : 'Hochladen' }}
                      </button>
                    </div>
                  </div>

                  <!-- Upload Result Alert -->
                  <div v-if="uploadResult" class="upload-result">
                    <div class="upload-result-success">
                      <div class="upload-result-header">
                        <font-awesome-icon icon="check-circle" />
                        <div class="upload-result-title">
                          <strong>Import abgeschlossen!</strong>
                        </div>
                      </div>

                      <div class="upload-result-summary">
                        {{ uploadResult.validRows || 0 }} von
                        {{ uploadResult.totalRows || 0 }} Datensätzen erfolgreich importiert
                        <span
                          v-if="uploadResult.validationErrors > 0"
                          class="upload-result-warning"
                        >
                          • {{ uploadResult.validationErrors }} Zeilen wegen Fehlern übersprungen
                        </span>
                        <span v-if="uploadResult.importErrors > 0" class="upload-result-error">
                          • {{ uploadResult.importErrors }} Zeilen konnten nicht gespeichert werden
                        </span>
                      </div>
                      <!-- Validierungsfehler -->
                      <div
                        v-if="
                          uploadResult.validationErrors && uploadResult.validationErrors.length > 0
                        "
                        class="upload-result-section"
                      >
                        <div class="upload-result-section-title upload-result-warning">
                          <strong>Übersprungene Zeilen:</strong>
                        </div>
                        <div class="upload-result-list">
                          <div
                            v-for="validationError in uploadResult.validationErrors"
                            :key="validationError.rowNumber"
                            class="upload-result-item"
                          >
                            <div class="upload-result-item-title">
                              Zeile {{ validationError.rowNumber }} übersprungen
                            </div>
                            <div class="upload-result-item-errors">
                              <div
                                v-for="error in validationError.errors"
                                :key="error"
                                class="upload-result-error-text"
                              >
                                {{ error }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Erfolgreiche Imports
                      <div v-if="uploadResult.successfulImports && uploadResult.successfulImports.length > 0" class="upload-result-section">
                        <div class="upload-result-section-title">
                          <strong>Erfolgreich importiert:</strong>
                        </div>
                        <div class="upload-result-list">
                          <div v-for="importItem in uploadResult.successfulImports" :key="importItem.id" class="upload-result-item">
                            <div class="upload-result-item-title">Zeile {{ importItem.rowNumber }}</div>
                            <div class="upload-result-item-data">
                              <div v-for="field in uploadResult.fields" :key="field.name" class="upload-result-data-item">
                                <span class="upload-result-data-label">{{ field.displayName }}:</span>
                                <span class="upload-result-data-value">{{ importItem.data[field.name] || '-' }}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div> -->
                    </div>
                  </div>

                  <!-- Upload Error Alert -->
                  <div v-if="uploadError" class="mt-3">
                    <div class="alert alert-danger" role="alert">
                      <div class="d-flex align-items-center">
                        <font-awesome-icon icon="exclamation-triangle" class="me-2" />
                        <div>
                          <strong>Import fehlgeschlagen!</strong>
                          <br />
                          {{ uploadError }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            {{ form.type === 'manual' ? 'Abbrechen' : 'Schließen' }}
          </button>
          <button
            type="button" 
            class="btn btn-primary"
            @click="saveDraft"
            :disabled="saving"
            v-if="form.type === 'manual'"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
            {{ saving ? 'Speichern...' : isEditing ? 'Aktualisieren' : 'Erstellen' }}
          </button>
        </div>
      </div>
    </div>
    </div>

    <!-- Modal Backdrop -->
    <div v-if="showModal" class="modal-backdrop fade show"></div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import teamTemplateDraftsService from '@/api/teamTemplateDrafts'
import { templateService } from '@/api/templates'
import { showSuccess, showError } from '@/services/notification'

const props = defineProps({
  showModal: {
    type: Boolean,
    default: false,
  },
  draft: {
    type: Object,
    default: null,
  },
  teamTemplateId: {
    type: String,
    required: false,
    default: null,
  },
  teamTemplate: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['draft-created', 'draft-updated', 'modal-closed'])


const saving = ref(false)
const form = ref({
  type: '',
  data: {},
})


const templateFields = ref([])
const manualData = ref({})



const templateFieldsByCategory = computed(() => {
  const fields = templateFields.value || []
  return {
    personal: fields.filter((field) => field.category === 'personal'),
    receipt: fields.filter((field) => field.category === 'receipt'),
    financial: fields.filter((field) => field.category === 'financial'),
  }
})


const isEditing = computed(() => !!props.draft)



const closeModal = () => {
  uploadResult.value = null
  emit('modal-closed')
}

const loadTemplateFields = async () => {
  if (!props.teamTemplate) return

  try {
    
    templateFields.value = (props.teamTemplate.template.fields || []).filter(
      (field) => field.type !== 'file',
    )

    
    manualData.value = {}
    templateFields.value.forEach((field) => {
      manualData.value[field.name] = ''

      
      if (field.options?.includeTaxRate) {
        let defaultTaxRate = ''
        if (field.options?.taxRateSelectionMode === 'user_choice') {
          const available = Array.isArray(field.options?.availableTaxRates)
            ? field.options.availableTaxRates
            : []
          if (
            field.options.selectedTaxRateId &&
            (!available.length || available.includes(field.options.selectedTaxRateId))
          ) {
            defaultTaxRate = field.options.selectedTaxRateId
          }
        } else if (field.options?.selectedTaxRate) {
          defaultTaxRate =
            field.options.selectedTaxRate.id || field.options.selectedTaxRateId || ''
        } else if (field.options?.selectedTaxRateId) {
          defaultTaxRate = field.options.selectedTaxRateId
        }
        manualData.value[`${field.name}_taxRate`] = defaultTaxRate || ''
      }
    })
  } catch (err) {
    console.error('Error loading template fields:', err)
  }
}

const downloadExcelTemplate = async () => {
  if (!templateFields.value.length) {
    showError('Keine Template-Felder verfügbar')
    return
  }

  if (!props.teamTemplateId) {
    showError('Kein Template ausgewählt')
    return
  }

  try {
    
    if (!props.teamTemplate) {
      showError('Bereich-Template nicht gefunden')
      return
    }
    
    const response = await templateService.downloadTemplateExcel(props.teamTemplate.id)

    
    const contentDisposition =
      response.headers?.['content-disposition'] || response.headers?.['Content-Disposition']
    let filename = 'template_fields.xlsx'
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
      if (filenameMatch) {
        filename = filenameMatch[1]
      }
    }

    
    const url = window.URL.createObjectURL(response.data)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)

    showSuccess('Excel-Vorlage erfolgreich heruntergeladen')
  } catch (err) {
    console.error('Error downloading excel template:', err)
    showError('Fehler beim Herunterladen der Excel-Vorlage')
  }
}

const selectedFile = ref(null)
const uploading = ref(false)
const uploadResult = ref(null)
const uploadError = ref(null)

const onExcelFileSelected = (event) => {
  const file = event.target.files[0]
  if (file) {
    selectedFile.value = file

    showSuccess('Excel-Datei ausgewählt: ' + file.name)
  }
}

const uploadExcelFile = async () => {
  if (!selectedFile.value) {
    uploadError.value = 'Keine Datei ausgewählt'
    return
  }

  if (!props.teamTemplateId) {
    uploadError.value = 'Kein Template ausgewählt'
    return
  }

  uploading.value = true
  uploadError.value = null
  uploadResult.value = null

  try {
    const result = await templateService.uploadTemplateExcel(
      props.teamTemplateId,
      selectedFile.value,
    )



    if (result.success) {
      uploadResult.value = result.data

      
      selectedFile.value = null
      
      const fileInput = document.getElementById('excelFileInput')
      if (fileInput) {
        fileInput.value = ''
      }
    } else {
      uploadError.value = 'Fehler beim Verarbeiten der Excel-Datei'
    }
  } catch (error) {
    console.error('Upload error:', error)

    if (error.response?.data?.details) {
      
      const errorDetails = error.response.data.details
      if (Array.isArray(errorDetails)) {
        uploadError.value = 'Validierungsfehler: ' + errorDetails.join(', ')
      } else {
        uploadError.value = error.response.data.error || 'Fehler beim Upload'
      }
    } else {
      uploadError.value = 'Fehler beim Upload der Excel-Datei'
    }
  } finally {
    uploading.value = false
  }
}

const selectType = (type) => {
  form.value.type = type
}


const onTypeChange = () => {
  if (form.value.type !== 'excel') {
    form.value.excelMapping = {}
  }
  
  if (form.value.type !== 'manual') {
    manualData.value = {}
  }
}

const saveDraft = async () => {
  
  if (!form.value.type) {
    showError('Bitte wähle einen Typ aus')
    return
  }

  if (!props.teamTemplateId) {
    showError('Kein Template ausgewählt')
    return
  }

  saving.value = true

  
  const draftData = {
    type: form.value.type,
    data: {},
  }

  
  if (form.value.type === 'manual') {
    
    const formattedData = {}

    templateFields.value.forEach((field) => {
      if (manualData.value[field.name] !== undefined && manualData.value[field.name] !== '') {
        formattedData[field.name] = {
          label: field.displayName,
          type: field.type,
          data: manualData.value[field.name],
          category: field.category || 'general',
        }

        
        if (field.options?.includeTaxRate && manualData.value[`${field.name}_taxRate`]) {
          formattedData[`${field.name}_taxRate`] = {
            label: `${field.displayName} Steuersatz`,
            type: 'select',
            data: manualData.value[`${field.name}_taxRate`],
            category: field.category || 'financial',
          }
        }
      }
    })

    draftData.data = formattedData
  }

  saving.value = true

  try {
    if (isEditing.value) {
      await teamTemplateDraftsService.updateTeamTemplateDraft(props.draft.id, draftData)
      emit('draft-updated')
    } else {
      await teamTemplateDraftsService.createTeamTemplateDraft(props.teamTemplateId, draftData)
      emit('draft-created')
    }
  } catch (err) {
    console.error('Error saving draft:', err)
    showError(err.response?.data?.error || 'Fehler beim Speichern der Vorlage')
  } finally {
    saving.value = false
  }
}

const resetForm = () => {
  form.value = {
    type: '',
    data: {},
  }

  
  manualData.value = {}


  
  if (templateFields.value.length > 0) {
    templateFields.value.forEach((field) => {
      manualData.value[field.name] = ''

      
      if (field.options?.includeTaxRate) {
        manualData.value[`${field.name}_taxRate`] = ''
      }
    })
  }
}

const loadDraftData = () => {
  if (props.draft) {
    form.value = { ...props.draft }
    
    if (props.draft?.data) {
      
      const simpleData = {}

      Object.keys(props.draft.data).forEach((key) => {
        const fieldData = props.draft.data[key]
        if (typeof fieldData === 'object' && fieldData.data !== undefined) {
          simpleData[key] = fieldData.data
        } else {
          
          simpleData[key] = fieldData
        }
      })

      manualData.value = simpleData
    }
  } else {
    resetForm()
  }
}



watch(
  () => props.showModal,
  (newVal) => {
    if (newVal) {
      loadDraftData()
      loadTemplateFields()
    }
  },
)

watch(
  () => props.draft,
  () => {
    if (props.showModal) {
      loadDraftData()
    }
  },
)

watch(
  () => props.teamTemplateId,
  () => {
    if (props.showModal && props.teamTemplateId) {
      loadTemplateFields()
    }
  },
)


onMounted(() => {
  if (props.showModal && props.teamTemplateId) {
    loadTemplateFields()
  }
})
</script>

<style scoped>
.modal-xl {
  max-width: 900px;
}

.form-control,
.form-select {
  font-size: 0.9rem;
}

textarea.form-control {
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.alert {
  font-size: 0.9rem;
}

.type-card {
  height: 140px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.type-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #dee2e6;
}

.type-card:focus {
  outline: none;
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

.type-card .card-body {
  height: 100%;
}
</style>
